import { memo } from 'react';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flote/ui';
import { ShoppingCart } from 'lucide-react';
import type { B2BWaveOrderSummary } from '@flote/contracts/http';

interface OrderSelectorProps {
  orders: B2BWaveOrderSummary[];
  selectedOrderId: string;
  onOrderChange: (orderId: string) => void;
  selectedOrder: B2BWaveOrderSummary | undefined;
  disabled?: boolean;
  error?: string;
  noOrderValue: string;
  orderRequired?: boolean;
}

export const OrderSelector = memo<OrderSelectorProps>(({
  orders,
  selectedOrderId,
  onOrderChange,
  selectedOrder,
  disabled = false,
  error,
  noOrderValue,
  orderRequired = false,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
        B2BWave Order
        {!orderRequired && <span className="text-xs font-normal text-muted-foreground">(optional)</span>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="orderId">Select Order</Label>
        <Select value={selectedOrderId} onValueChange={onOrderChange} disabled={disabled}>
          <SelectTrigger className={error ? 'border-destructive' : ''}>
            <SelectValue placeholder={orderRequired ? 'Select an order' : 'Select an order (optional)'} />
          </SelectTrigger>
          <SelectContent>
            {!orderRequired && (
              <SelectItem value={noOrderValue}>No order attached</SelectItem>
            )}
            {orders.map((order) => (
              <SelectItem key={order.id} value={order.id.toString()}>
                Order #{order.id} - {order.customerCompany || 'Unknown'} (${order.grossTotal})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Link this delivery task to a B2BWave order
        </p>
      </div>

      {/* Selected Order Details */}
      {selectedOrder && (
        <div className="rounded-md border p-4 bg-muted/30 space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium">Order #{selectedOrder.id}</p>
              <p className="text-sm text-muted-foreground">{selectedOrder.customerCompany}</p>
              {selectedOrder.customerName && (
                <p className="text-xs text-muted-foreground">{selectedOrder.customerName}</p>
              )}
            </div>
            <div className="text-right">
              <p className="font-semibold text-lg">${selectedOrder.grossTotal}</p>
              {selectedOrder.currency && (
                <p className="text-xs text-muted-foreground">{selectedOrder.currency}</p>
              )}
            </div>
          </div>

          {/* Products Table */}
          {selectedOrder.orderProducts.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Products ({selectedOrder.orderProducts.length})
              </p>
              <div className="rounded border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs py-2">SKU</TableHead>
                      <TableHead className="text-xs py-2">Product</TableHead>
                      <TableHead className="text-xs py-2 text-right">Qty</TableHead>
                      <TableHead className="text-xs py-2 text-right">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedOrder.orderProducts.slice(0, 5).map((product) => (
                      <TableRow key={product.id}>
                        <TableCell className="text-xs py-1.5 font-mono">
                          {product.productCode || '-'}
                        </TableCell>
                        <TableCell className="text-xs py-1.5 max-w-[150px] truncate">
                          {product.productName || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-xs py-1.5 text-right">
                          {product.quantity}
                        </TableCell>
                        <TableCell className="text-xs py-1.5 text-right">
                          ${product.price}
                        </TableCell>
                      </TableRow>
                    ))}
                    {selectedOrder.orderProducts.length > 5 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-xs py-1.5 text-center text-muted-foreground">
                          +{selectedOrder.orderProducts.length - 5} more products
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Customer Comments */}
          {selectedOrder.commentsCustomer && (
            <div className="text-xs">
              <span className="font-medium">Customer Note:</span>
              <span className="ml-1 text-muted-foreground">{selectedOrder.commentsCustomer}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

OrderSelector.displayName = 'OrderSelector';
